import { parseFloatFromString, subtractDecimalStrings } from './formatters';

const INSTRUMENT_TYPE_LABELS = { stock: 'Stocks', etf: 'ETFs', fund: 'Funds' };

export const instrumentTypeLabel = (type) => INSTRUMENT_TYPE_LABELS[String(type || '').toLowerCase()] || type || 'Unknown';

// Keep native metadata in filter values, separate from presentation labels. Missing
// sector buckets mirror the dashboard's ETF/fund fallback without matching a real
// sector that happens to be named "ETFs", "Funds" or "Unclassified".
export const stockSectorValue = (token) => {
  if (token?.sector) return `sector:${token.sector}`;
  const type = String(token?.instrumentType || '').toLowerCase();
  return type === 'etf' || type === 'fund' ? `missing:${type}` : 'missing:sector';
};

export const stockInstrumentTypeValue = (token) => token?.instrumentType || 'missing:type';

export const stockMetadataOptions = (positions, dimension) => {
  const options = new Map();
  (positions || []).forEach(({ token }) => {
    const value = dimension === 'sector' ? stockSectorValue(token) : stockInstrumentTypeValue(token);
    const label = dimension === 'sector' ? stockSectorLabel(token) : instrumentTypeLabel(token?.instrumentType);
    options.set(value, { value, label });
  });
  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
};

// ETFs and funds have no sector, so they are grouped by instrument type instead.
export const stockSectorLabel = (token) => {
  if (token?.sector) return token.sector;
  const type = String(token?.instrumentType || '').toLowerCase();
  return type === 'etf' || type === 'fund' ? instrumentTypeLabel(type) : 'Unclassified';
};

const toCategories = (byName) => Array.from(byName.values())
  .map(({ name, value, positionIds, filterValue }) => ({ name, value: String(value), positionCount: positionIds.size, ...(filterValue !== undefined ? { filterValue } : {}) }))
  .sort((a, b) => parseFloatFromString(b.value) - parseFloatFromString(a.value));

const addTo = (byName, name, positionId, value, filterValue) => {
  if (!name || !(value > 0)) return;
  const key = filterValue ?? name;
  const entry = byName.get(key) || { name, value: 0, positionIds: new Set(), filterValue };
  entry.value += value;
  entry.positionIds.add(positionId);
  byName.set(key, entry);
};

// Groups holdings value (already in portfolio currency) by a label derived from each position.
export const stockCategoriesBy = (positions, labelFor, valueFor) => {
  const byName = new Map();
  (positions || []).forEach((position) => {
    addTo(byName, labelFor(position), position.id, parseFloatFromString(position.holdingsValue || '0'), valueFor?.(position));
  });
  return toCategories(byName);
};

// Splits each position's holdings value across its locations by quantity share, so
// listing-currency prices never enter the math. Remaining quantity counts as Unassigned.
export const stockLocationCategories = (positions) => {
  const byName = new Map();
  (positions || []).forEach((position) => {
    const holdings = parseFloatFromString(position.holdingsAmount || '0');
    const value = parseFloatFromString(position.holdingsValue || '0');
    if (!(holdings > 0) || !(value > 0)) return;
    const valuePerShare = value / holdings;
    const balances = position.locationBalances || [];
    balances.forEach((balance) => {
      addTo(byName, balance.name || 'Unknown', position.id, parseFloatFromString(balance.quantity || '0') * valuePerShare);
    });
    const unassigned = subtractDecimalStrings(position.holdingsAmount || '0', balances.map((b) => b.quantity || '0'));
    addTo(byName, 'Unassigned', position.id, parseFloatFromString(unassigned) * valuePerShare);
  });
  return toCategories(byName);
};
